import styled from '@emotion/styled'
import { AdmRuleHistory, LazyLoadingRule } from '@pubstack/common/src/adm/rules'
import { FunctionComponent, useState } from 'react'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { HistoryFlyout } from '~/components/HistoryFlyout'
import { Icon } from '~/components/Icon'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Link } from '~/components/Link'
import { PageContent } from '~/components/PageContent'
import { Pagination } from '~/components/Pagination'
import { Tooltip } from '~/components/Tooltip'
import Table, { EmptyTable, handleTableSearch, handleTableSort, onColumnSort, SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { ArchiveRuleModal } from '~/feature-components/ArchiveRuleModal'
import { RuleListToolbar } from '~/feature-components/RuleListToolbar'
import { useClientPagination } from '~/hooks/useClientPagination'
import { countStacksUse } from '~/modules/adm/rules/countStacksUsed'
import { PureUsedStackTooltip } from '~/modules/adm/rules/PureUsedStackTooltip'
import { RuleDetailsFlyout } from '~/modules/adm/rules/RuleDetailsFlyout'
import { WithClassName } from '~/types/utils'
import { SUPPORT_MAIL } from '~/utils/constants'
import { PureAdmLazyLoadingHelpFlyout } from './PureAdmLazyLoadingHelpFlyout'

type PureAdmLazyLoadingPageProps = WithClassName & {
  lazyLoadingRules: LazyLoadingRule[]
  isLoading: boolean
  isError: boolean
  breadcrumbs: React.ReactNode
  onCreateRule: () => void
  onEditRule: (id: string) => void
  lazyLoadingRulesHistory?: AdmRuleHistory
  onArchiveRule: (id: string) => void | Promise<unknown>
  isArchiving: boolean
  onNavigateToArchived: () => void
  canCreate: boolean
  canArchive: boolean
  /** What a non-owner is told on the controls they cannot press. */
  permissionDisabledReason?: string
}

const ActionsTableCell = styled(TableCell)`
  width: 0px; /** necessary to get the last cell to shrink down to hug action buttons */
`

const ButtonActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const HeaderBiddingIconWrapper = styled.div`
  display: flex;
`

type LazyLoadingRuleListed = Omit<LazyLoadingRule, 'creationTime' | 'technicalId'>

const FLYOUT_WIDTH = '600px'

const UnstyledPureAdmLazyLoadingPage: FunctionComponent<PureAdmLazyLoadingPageProps> = ({
  className,
  lazyLoadingRules,
  isLoading,
  isError,
  breadcrumbs,
  onCreateRule,
  onEditRule,
  lazyLoadingRulesHistory,
  onArchiveRule,
  isArchiving,
  onNavigateToArchived,
  canCreate,
  canArchive,
  permissionDisabledReason,
}) => {
  const [columns, setColumns] = useState<TableColumns<LazyLoadingRuleListed>>([
    {
      name: 'Rule',
      isSortable: true,
      order: 'none',
      attributeSort: 'name',
    },
    {
      name: 'HB',
      isSortable: true,
      order: 'none',
      attributeSort: 'headerBiddingEnabled',
    },
    {
      name: 'GAM',
      isSortable: true,
      order: 'none',
      attributeSort: 'gamEnabled',
    },
    {
      name: 'Auction start',
      isSortable: true,
      order: 'none',
      attributeSort: 'prebidAuctionViewportsPercent',
    },
    {
      name: 'GAM request',
      isSortable: true,
      order: 'none',
      attributeSort: 'googletagFetchViewportsPercent',
    },
    {
      name: 'Render impression',
      isSortable: true,
      order: 'none',
      attributeSort: 'googletagRenderViewportsPercent',
    },
    {
      name: 'Used on',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'stacksUse',
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ])

  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()
  const [search, setSearch] = useState('')
  // Keep the table empty on error, otherwise the synthetic 'NO_RULE' row would hide the error state
  const lazyLoadingRulesWithNoRule: LazyLoadingRuleListed[] = isError ? [] : [...lazyLoadingRules, { id: 'NO_RULE', name: 'No lazy loading', updatedBy: 'Pubstack', updatedAt: new Date() }]
  const searchedLLRules = handleTableSearch(lazyLoadingRulesWithNoRule, search, ['name'])
  const sortedColumn = (columns.find((c) => c.isSortable && c.order !== 'none') || columns[0]) as SortableColumn<LazyLoadingRule>
  let displayLlRules: LazyLoadingRuleListed[] = []
  if (sortedColumn.attributeSort === 'stacksUse') {
    displayLlRules = searchedLLRules.sort((a, b) => (sortedColumn.order === 'ascending' ? 1 : -1) * (countStacksUse(b.stacksUse) - countStacksUse(a.stacksUse)))
  } else {
    displayLlRules = handleTableSort(columns, searchedLLRules)
  }

  const { pageItems, paginationProps, resetPage } = useClientPagination(displayLlRules)

  /**
   * Why this row cannot be archived, or `undefined` when it can.
   *
   * The same refusals the API makes, said before the click rather than after it. A rule with no
   * `scopeId` is one of Pubstack's globals, shared by every scope — the same test that already decides
   * whether the pen is enabled.
   */
  const archiveDisabledReason = (llRule: LazyLoadingRuleListed): string | undefined => {
    if (!llRule.scopeId) {
      return "Can't archive a Pubstack rule"
    }
    const stackCount = countStacksUse(llRule.stacksUse)
    if (stackCount > 0) {
      return `Used on ${stackCount} stack${stackCount === 1 ? '' : 's'}. Remove it from them first.`
    }
    return canArchive ? undefined : permissionDisabledReason
  }

  /**
   * The synthetic `NO_RULE` row is a rule only as far as the table is concerned: it has no settings and no
   * stacks, and every cell above already special-cases it. So the flyout says the one true thing about it
   * rather than printing a row of dashes.
   */
  const openDetails = (llRule: LazyLoadingRuleListed) =>
    flyout.open(
      RuleDetailsFlyout,
      { color: Colors.Lilac, width: FLYOUT_WIDTH, closeTrackingLabel: 'adm-lazy-loading-rules-close' },
      {
        name: llRule.name,
        details:
          llRule.id === 'NO_RULE'
            ? [{ label: 'Lazy loading', value: 'Off — ad units on a stack using this rule load with the page.' }]
            : [
                { label: 'Header bidding', value: llRule.headerBiddingEnabled ? 'Enabled' : 'Disabled' },
                { label: 'GAM', value: llRule.gamEnabled ? 'Enabled' : 'Disabled' },
                { label: 'Prebid auction', value: llRule.headerBiddingEnabled ? `${llRule.prebidAuctionViewportsPercent} viewports` : '—' },
                { label: 'Googletag fetch', value: llRule.googletagFetchViewportsPercent !== undefined ? `${llRule.googletagFetchViewportsPercent} viewport(s)` : '—' },
                { label: 'Googletag render', value: llRule.googletagRenderViewportsPercent !== undefined ? `${llRule.googletagRenderViewportsPercent} viewport(s)` : '—' },
              ],
        stacksUse: llRule.stacksUse,
        onEdit: llRule.scopeId
          ? () => {
              flyout.close()
              onEditRule(llRule.id)
            }
          : undefined,
        editDisabledReason: 'This is a Pubstack rule, so it cannot be edited.',
        editTrackingLabel: 'adm-lazy-loading-rules-edit',
      }
    )

  return (
    <PageContent
      className={className}
      breadcrumbs={breadcrumbs}
      /* Help alone up here now. Creating a rule acts on *this list*, so it moved down beside the rest of
         what is done to it, leaving the title bar to hold only what is about the page itself. */
      headerActions={
        <Button
          variant={'tertiary'}
          iconName={'help'}
          onClick={() => flyout.open(PureAdmLazyLoadingHelpFlyout, { color: Colors.Lilac, closeTrackingLabel: 'adm-lazy-loading-rules-help-close' })}
          trackingLabel={'adm-lazy-loading-rules-help'}
        >
          Help
        </Button>
      }
    >
      <RuleListToolbar
        search={search}
        onSearchChange={(next) => {
          setSearch(next)
          resetPage()
        }}
        onOpenHistory={
          lazyLoadingRulesHistory
            ? () =>
                flyout.open(HistoryFlyout, { color: Colors.King, width: '816px', closeTrackingLabel: 'adm-lazy-loading-history-close' }, { item: 'Lazy loading rules', data: lazyLoadingRulesHistory })
            : undefined
        }
        historyTrackingLabel={'adm-lazy-loading-history-view'}
        createLabel={'New rule'}
        onCreate={onCreateRule}
        createTrackingLabel={'adm-lazy-loading-rules-new'}
        canCreate={canCreate}
        createDisabledReason={permissionDisabledReason}
        canSeeArchived={canArchive}
        archivedLabel={'Archived rules'}
        onNavigateToArchived={onNavigateToArchived}
        archivedTrackingLabel={'adm-lazy-loading-rules-archived-navigate'}
        actionsTrackingLabel={'adm-lazy-loading-rules-actions-view'}
      />
      <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {isError && (
          <TableRow>
            <TableCell colspan={columns.length}>
              <EmptyTable>
                <div>
                  <h2>An error occurred when trying to display your lazy loading rules</h2>
                  <p>
                    Please refresh the page or contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} /> if the issue persists.
                  </p>
                </div>
              </EmptyTable>
            </TableCell>
          </TableRow>
        )}
        {pageItems.map((llRule) => (
          /* Every row opens the details, the Pubstack-owned "No lazy loading" one included: reading a rule is
             not editing it, so the row that could not be clicked at all now has something to show. */
          <TableRow key={llRule.id} clickable={{ onClick: () => openDetails(llRule), trackingLabel: 'adm-lazy-loading-rules-view' }}>
            <TableCell>{llRule.name}</TableCell>
            <TableCell>
              <HeaderBiddingIconWrapper>{llRule.headerBiddingEnabled ? <Icon name={'check'} fill={Colors.Success} /> : <Icon name={'close'} fill={Colors.Ash} />}</HeaderBiddingIconWrapper>
            </TableCell>
            <TableCell>
              <HeaderBiddingIconWrapper>{llRule.gamEnabled ? <Icon name={'check'} fill={Colors.Success} /> : <Icon name={'close'} fill={Colors.Ash} />}</HeaderBiddingIconWrapper>
            </TableCell>
            <TableCell>{llRule.headerBiddingEnabled ? `${llRule.prebidAuctionViewportsPercent} viewports` : `-`}</TableCell>
            <TableCell>{llRule.googletagFetchViewportsPercent !== undefined ? `${llRule.googletagFetchViewportsPercent} viewport(s)` : `-`}</TableCell>
            <TableCell>{llRule.googletagRenderViewportsPercent !== undefined ? `${llRule.googletagRenderViewportsPercent} viewport(s)` : `-`}</TableCell>
            <TableCell>{llRule.id === 'NO_RULE' ? '-' : llRule.stacksUse ? <PureUsedStackTooltip stacksBySite={llRule.stacksUse} /> : <span>0 stack</span>}</TableCell>
            <ActionsTableCell>
              <ButtonActions>
                <Tooltip title={'View'} positions={['left', 'bottom', 'top', 'right']}>
                  <Button
                    iconName={'view'}
                    variant={'tertiary'}
                    title={'View'}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      openDetails(llRule)
                    }}
                    trackingLabel={'adm-lazy-loading-rules-icon-view'}
                  />
                </Tooltip>
                <Tooltip title={!llRule.scopeId ? `Can't edit a Pubstack rule` : 'Edit'} positions={['left', 'bottom', 'top', 'right']}>
                  <Button
                    iconName={'edit'}
                    variant={'tertiary'}
                    title={'Edit'}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      onEditRule(llRule.id)
                    }}
                    disabled={!llRule.scopeId}
                    trackingLabel={'adm-lazy-loading-rules-icon-edit'}
                  />
                </Tooltip>
                {/* Owners only, and only for a rule nothing uses — the button stays on the row saying
                    which of the two it is, rather than disappearing and leaving the reader to guess
                    whether archiving exists at all. */}
                {canArchive && (
                  <Tooltip title={archiveDisabledReason(llRule) ?? 'Archive'} positions={['left', 'bottom', 'top', 'right']}>
                    <Button
                      iconName={'archive'}
                      variant={'tertiary'}
                      title={'Archive'}
                      disabled={!!archiveDisabledReason(llRule) || isArchiving}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        modal.open(ArchiveRuleModal, {
                          name: llRule.name,
                          noun: 'lazy loading rule',
                          onArchive: () => onArchiveRule(llRule.id),
                          isPending: isArchiving,
                          trackingPrefix: 'adm-lazy-loading-rules-archive',
                        })
                      }}
                      trackingLabel={'adm-lazy-loading-rules-archive'}
                    />
                  </Tooltip>
                )}
              </ButtonActions>
            </ActionsTableCell>
          </TableRow>
        ))}
      </Table>
      <Pagination {...paginationProps} className={'mt-4'} />
    </PageContent>
  )
}

export const PureAdmLazyLoadingPage = styled(UnstyledPureAdmLazyLoadingPage)``
